import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FileText, Lightbulb, PlayCircle } from 'lucide-react';
import { ActionStack, Screen } from '@/app/layout/Screen';
import { Button } from '@/components/ui/Button';
import { LoadFailure, PanelSkeleton } from '@/components/ui/Feedback';
import { Sheet } from '@/components/ui/Sheet';
import { FilePreview, type PreviewedFile } from '@/components/ui/FilePreview';
import { TaskText } from '@/components/ui/TaskText';
import { Badge, Helper, Kicker, MiniIcon, PageTitle, Panel, Row, SectionHeading } from '@/components/ui/Surface';
import { OptionCard } from '@/components/ui/Form';
import { messageOf } from '@/api/miniapp/client';
import { miniappApi } from '@/api/miniapp/miniappApi';
import { getBridge } from '@/platform';
import { useUiStore } from '@/store/uiStore';
import type { LessonDto, LessonFile } from '@/api/miniapp/types';
import { cleanLessonTitle } from '@/lib/titles';
import { lessonFileLabel, lessonFileName } from '@/lib/fileName';
import { useSavedDialog } from './useSavedDialog';

/**
 * Экран 10 на реальном бэкенде. Урок и задание к нему.
 *
 * Задание выполняется диалогом с ИИ: кнопка ведёт в чат по `assignment_id`.
 * Инструкция и подсказка приходят с сервера отдельными полями и показываются
 * листами, а не сразу — иначе задание тонет в тексте.
 *
 * Состояния: загрузка, ошибка с повтором, урок, сданный урок.
 */
export function BackendLessonScreen() {
  const { assignmentId = '' } = useParams();
  const [searchParams] = useSearchParams();
  // Инструмент открыт из вкладки «ИИ»: она и должна оставаться активной,
  // иначе пользователь думает, что ушёл в другой раздел
  const fromTools = searchParams.get('from') === 'ai';
  const tab = fromTools ? 'ai' : 'course';
  const backTo = fromTools ? '/assistant' : undefined;
  const navigate = useNavigate();
  const toast = useUiStore((state) => state.toast);

  const [lesson, setLesson] = useState<LessonDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState<'instruction' | 'hint' | null>(null);
  /** Открытый файл задания: показывается внутри приложения. */
  const [preview, setPreview] = useState<(PreviewedFile & { source: string; blob: Blob }) | null>(null);
  // Часть заданий предлагает варианты: без выбора диалог начинать нельзя,
  // иначе ассистент получит задание без условия
  const [variantId, setVariantId] = useState<number | null>(null);
  // Переписка сохраняется: кнопка должна обещать то, что за ней откроется
  const savedDialog = useSavedDialog(Boolean(assignmentId));

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await miniappApi.getLesson(assignmentId);
        if (!cancelled) setLesson(data);
      } catch (cause) {
        if (!cancelled) {
          setError(messageOf(cause, 'Урок не загрузился'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (assignmentId) void load();
    return () => {
      cancelled = true;
    };
  }, [assignmentId]);

  if (loading) {
    return (
      <Screen subtitle={fromTools ? 'Инструмент ИИ' : 'Урок'} back backTo={backTo} tab={tab}>
        <PanelSkeleton count={3} />
      </Screen>
    );
  }

  if (error || !lesson) {
    return (
      <Screen subtitle={fromTools ? 'Инструмент ИИ' : 'Урок'} back backTo={backTo} tab={tab}>
        <LoadFailure
          title="Урок не загрузился"
          description={error}
          onRetry={() => navigate(0)}
          secondary={
            <Button variant="ghost" block onClick={() => navigate('/course')}>
              Вернуться к программе
            </Button>
          }
        />
      </Screen>
    );
  }

  /**
   * Файл открывается просмотром, а не скачиванием.
   *
   * Загрузку из мини-приложения часть клиентов запрещает: нажатие
   * заканчивалось ничем — файл не открывался и не сохранялся. Просмотр внутри
   * работает везде, а сохранение осталось отдельной кнопкой внутри.
   */
  const openFile = async (file: LessonFile, index: number) => {
    let blob: Blob;
    try {
      blob = await miniappApi.downloadLessonFile(file.url);
    } catch {
      toast('Файл недоступен', 'error');
      return;
    }

    setPreview({
      url: URL.createObjectURL(blob),
      name: lessonFileName(file, index),
      contentType: typeof file.content_type === 'string' ? file.content_type : undefined,
      source: file.url,
      blob,
    });
  };

  const closePreview = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const saveFile = async () => {
    if (!preview) return;

    /**
     * Полный адрес, а не относительный.
     *
     * Файлы задания лежат в открытой раздаче и сессии не требуют, поэтому
     * клиент может скачать их сам — этот путь работает там, где загрузка
     * ссылкой из мини-приложения запрещена и нажатие заканчивалось ничем.
     * Готовый файл передаётся рядом как запасной путь.
     */
    // Готовый файл берётся из просмотра, а не запрашивается заново: MAX
    // пропускает загрузку только сразу после нажатия, и любое ожидание перед
    // вызовом моста превращает нажатие в «ничего не произошло»
    const ok = await getBridge().downloadFile({
      url: new URL(preview.source, window.location.origin).toString(),
      fileName: preview.name,
      blob: preview.blob,
    });
    if (!ok) toast('Клиент запретил скачивание файла', 'error');
  };

  const variants = lesson.variants ?? [];
  const selected = variants.find((variant) => variant.variant_id === variantId) ?? null;

  // Текст, инструкция и подсказка берутся у выбранного варианта: у него своё условие
  const description = selected?.description ?? lesson.description;
  const instruction = selected?.instruction ?? lesson.instruction;
  const hint = selected?.hint ?? lesson.hint;
  const readyToStart = variants.length === 0 || selected !== null;

  return (
    <Screen subtitle={fromTools ? 'Инструмент ИИ' : 'Урок'} back backTo={backTo} tab={tab}>
      {/* Инструмент ассистента — не урок курса: нумерация заданий там лишняя */}
      {fromTools ? null : <Kicker>Задание {lesson.order}</Kicker>}
      <PageTitle>{cleanLessonTitle(lesson.title)}</PageTitle>
      {lesson.completed ? <Badge tone="success">Сдано</Badge> : null}

      {variants.length > 0 ? (
        <>
          <SectionHeading>Выберите вариант</SectionHeading>
          {variants.map((variant) => (
            <OptionCard
              key={variant.variant_id}
              title={variant.title}
              subtitle={firstLine(variant.description)}
              selected={variant.variant_id === variantId}
              onSelect={() => setVariantId(variant.variant_id)}
            />
          ))}
        </>
      ) : null}

      <Panel className="mt-3">
        <TaskText text={description} />
      </Panel>

      {instruction ? (
        <Panel tone="tint" className="mt-2.5" interactive onClick={() => setSheet('instruction')}>
          <Row
            icon={
              <MiniIcon tone="lime">
                <FileText className="h-[17px] w-[17px]" aria-hidden />
              </MiniIcon>
            }
            title="Как выполнять"
            subtitle="Инструкция и правила работы с ассистентом"
            chevron
          />
        </Panel>
      ) : null}

      {hint ? (
        <Panel className="mt-2.5" interactive onClick={() => setSheet('hint')}>
          <Row
            icon={
              <MiniIcon tone="lime">
                <Lightbulb className="h-[17px] w-[17px]" aria-hidden />
              </MiniIcon>
            }
            title="Подсказка"
            subtitle="Если не знаете, с чего начать"
            chevron
          />
        </Panel>
      ) : null}

      {lesson.additional_materials ? (
        <Panel className="mt-2.5">
          <h3 className="mb-1.5 text-[13px] font-medium">Дополнительные материалы</h3>
          {/*
            Тот же разбор, что и у задания: материалы приходят списком
            определений, и сплошной приглушённый абзац на телефоне не читается
          */}
          <TaskText text={lesson.additional_materials} />
        </Panel>
      ) : null}

      {lesson.files && lesson.files.length > 0 ? (
        <Panel className="mt-2.5">
          <h3 className="mb-1.5 text-[13px] font-medium">Файлы по заданию</h3>
          {lesson.files.map((file, index) => (
            <Button
              key={file.url}
              block
              className="mt-2 min-h-[40px] text-[12px]"
              icon={<FileText className="h-4 w-4" aria-hidden />}
              onClick={() => void openFile(file, index)}
            >
              {lessonFileLabel(file, index)}
            </Button>
          ))}
        </Panel>
      ) : null}

      <ActionStack>
        <Button
          variant="primary"
          block
          disabled={!readyToStart}
          icon={<PlayCircle className="h-4 w-4" aria-hidden />}
          onClick={() =>
            navigate(
              [
                `/homework/${lesson.assignment_id}/chat`,
                [selected ? `variant=${selected.variant_id}` : '', fromTools ? 'from=ai' : '']
                  .filter(Boolean)
                  .join('&'),
              ]
                .filter(Boolean)
                .join('?')
            )
          }
        >
          {/*
            У инструмента ассистента нет «выполнения задания»: там просто
            диалог, и кнопка называется так же, как то, что за ней откроется
          */}
          {lesson.completed || fromTools
            ? savedDialog
              ? 'Продолжить диалог'
              : 'Начать диалог'
            : 'Начать выполнение задания'}
        </Button>
        {!readyToStart ? <Helper>Выберите вариант задания, чтобы начать</Helper> : null}
      </ActionStack>

      <FilePreview file={preview} onClose={closePreview} onSave={() => void saveFile()} />

      <Sheet
        open={sheet !== null}
        onClose={() => setSheet(null)}
        title={sheet === 'hint' ? 'Подсказка' : 'Как выполнять задание'}
      >
        <TaskText text={(sheet === 'hint' ? hint : instruction) ?? ''} className="text-[13px]" />
        <Button variant="primary" block className="mt-4" onClick={() => setSheet(null)}>
          Понятно
        </Button>
      </Sheet>
    </Screen>
  );
}

/** Первая содержательная строка описания — как краткая подпись варианта. */
function firstLine(text: string | null | undefined): string {
  const line = (text ?? '')
    .split('\n')
    .map((item) => item.trim())
    .find((item) => item && item !== 'Задание');
  return line ? line.slice(0, 90) : '';
}
